import { z } from "zod";
export const UNIVERSAL_VERIFICATION_PROTOCOL = "excellent.universal-verification.v1";
export const protocolVersionSchema = z.literal(UNIVERSAL_VERIFICATION_PROTOCOL);
export const protocolIdSchema = z.string().trim().min(1).max(200);
/**
 * A canonical Work identifier, as the protocol actually mints them.
 *
 * Work ids are `protocolIdSchema` values — opaque, tenant-scoped, bounded —
 * carrying a `work:` prefix and one or more `:`-separated segments. A minter
 * chooses the segments: a bare UUID (`work:<uuid>`), or a structured lineage
 * key such as `work:coding:real-repo-work:reds-verif:8bea7054008d-r3`,
 * `work:coding:universal-canary:<hex>:<hex>`, or `work:ui-gate:12460`.
 *
 * This schema is deliberately no narrower than the ids the protocol mints. A
 * route-level grammar that admits only ONE minter's shape does not validate
 * input, it refuses the tenant's own records — so the only constraints here are
 * the ones that are true of every Work id: the prefix, non-empty segments, and
 * the `protocolIdSchema` length bound. Segment characters are restricted to
 * URL-path-safe alphanumerics, `-` and `_`, which excludes `/`, `.`,
 * whitespace, control and percent-encoded characters from an id that is read
 * out of a path. `_` is admitted because minters put a `workType` in a segment
 * (`work:code_change:<sha>`, see adapters/git/public-dogfood.ts).
 */
export const universalWorkIdSchema = protocolIdSchema.regex(/^work:[A-Za-z0-9][A-Za-z0-9_-]*(?::[A-Za-z0-9][A-Za-z0-9_-]*)*$/, "expected a canonical work:<segment>[:<segment>…] identifier");
export const protocolVersionIdSchema = z.string().trim().min(1).max(100);
export const protocolTimestampSchema = z.string().datetime({ offset: true });
export const sha256DigestSchema = z.string().regex(/^sha256:[0-9a-f]{64}$/);
export const uriSchema = z.string().trim().min(1).max(4096);
/**
 * The verdict vocabulary. Four of these predate 2026-09-12; `MISMATCH` closes
 * the half of §5.4's gap that let "the evidence shows this was done wrong" and
 * "we could not tell" both land on not-PASS.
 *
 *   PASS      every obligation holds, at or above the authority floor.
 *   MISMATCH  an independent record was consulted and CONTRADICTED the claim.
 *             An accusation. Only the oracle seam can mint one
 *             (`protocol/oracle-resolution.ts`) — see `evaluatorResultSchema`
 *             in engine.ts for why an evaluator deliberately cannot.
 *   FAIL      conclusive, not PASS, and no independent contradiction was
 *             demonstrated. A refusal to certify. Every FAIL ever signed still
 *             means this; MISMATCH is a strict refinement, never a re-reading.
 *   UNKNOWN   no finding — coverage is partial or integrity did not hold, and
 *             no action by the submitter or the clock will change that. A
 *             HUMAN is asked to fix an instrument.
 *   NOT_RUN   nothing was looked at.
 *   PENDING   TOO EARLY TO TELL, and there is a deadline by which it will be.
 *             Added 2026-09-12; the other half of §5.4's gap. Distinct from
 *             NOT_RUN ("we did not look") and from UNKNOWN ("we looked and
 *             could not tell") by its CONSEQUENCE: a pending verdict re-queues
 *             a job and pages nobody, where UNKNOWN escalates. It is the ONLY
 *             status that is not self-contained — a PENDING is always
 *             accompanied by an `expiresAt` and by the status it becomes at
 *             that deadline. `resolvePendingVerdict` in `verdict-law.ts` is
 *             the total function that applies that deadline at read time —
 *             but see the warning there: no product reader calls it yet, so
 *             a stored PENDING does currently read PENDING past its deadline.
 *             Never PASS at the deadline (§5.4), by construction: see
 *             `PendingResolution`.
 *
 * Ranked, once, in `verification/verdict-law.ts`. Adding a value here without
 * ranking it there is a COMPILE error, not a silent default.
 */
export const verificationStatusSchema = z.enum([
    "PASS",
    "MISMATCH",
    "FAIL",
    "UNKNOWN",
    "NOT_RUN",
    "PENDING",
]);
export const assuranceClassSchema = z.enum(["PROSPECTIVE", "RETROSPECTIVE"]);
export const evaluatorAuthoritySchema = z.enum([
    "deterministic",
    "semantic",
    "human",
    "imported",
]);
export const consequenceClassSchema = z.enum([
    "low",
    "medium",
    "high",
    "critical",
]);
export const signatureSchema = z
    .object({
    keyId: protocolIdSchema,
    algorithm: z.literal("Ed25519"),
    value: z.string().min(1),
})
    .strict();
export const contractBindingSchema = z
    .object({
    tenantId: protocolIdSchema,
    workItemId: protocolIdSchema,
    contractId: protocolIdSchema,
    contractVersion: protocolVersionIdSchema,
    contractDigest: sha256DigestSchema,
})
    .strict();
export const attemptBindingSchema = contractBindingSchema
    .extend({
    attemptId: protocolIdSchema,
    inputSnapshotDigest: sha256DigestSchema,
})
    .strict();
export const evidenceBindingSchema = attemptBindingSchema
    .extend({
    outputSnapshotDigest: sha256DigestSchema,
    evidenceGraphDigest: sha256DigestSchema,
})
    .strict();
export function sameContractBinding(left, right) {
    return (left.tenantId === right.tenantId &&
        left.workItemId === right.workItemId &&
        left.contractId === right.contractId &&
        left.contractVersion === right.contractVersion &&
        left.contractDigest === right.contractDigest);
}
export function contractBindingPrefix(binding) {
    return contractBindingSchema.parse({
        tenantId: binding.tenantId,
        workItemId: binding.workItemId,
        contractId: binding.contractId,
        contractVersion: binding.contractVersion,
        contractDigest: binding.contractDigest,
    });
}
export function sameAttemptBinding(left, right) {
    return (sameContractBinding(left, right) &&
        left.attemptId === right.attemptId &&
        left.inputSnapshotDigest === right.inputSnapshotDigest);
}
export function sameEvidenceBinding(left, right) {
    return (sameAttemptBinding(left, right) &&
        left.outputSnapshotDigest === right.outputSnapshotDigest &&
        left.evidenceGraphDigest === right.evidenceGraphDigest);
}
